import {
  MISSION_MONITORING_STATIC_STATUS,
  MiSSION_MONITORING_COMMENT_COUNT,
  MiSSION_MONITORING_FLAG_COUNT,
  MiSSION_MONITORING_SUBSTATUS,
} from "../../constants";
import { getStatusColor, humanize, sumKey } from "../../utils";

import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import React from "react";

// Monitoring recap: a full-width synthesis band — a delivered-completion hero on
// the left, a 100%-stacked acquisition-status bar + legend filling the width, and
// flag/comment signals on the right. Clicking a status drills into its
// substatuses (same colouring as the monitoring dashboard); an optional "See all
// details" link jumps to the dashboard pre-filtered on the current scope.
// Level-agnostic: reused as-is for both a whole campaign and a single site — fed
// by already-fetched missions; the caller only populates them for users with the
// monitoring role.
// Fully controlled on the status: the drill level is `selectedStatus`, owned by
// the caller, which also owns the list filter. A local copy here would drift the
// two apart (clearing the filter left the band drilled, and picking the status
// from the filter bar left it closed).
export const MonitoringRecap = ({
  missions,
  detailTo,
  loading,
  selectedStatus,
  onSelectStatus,
}) => {
  // Shimmer skeleton while missions load, so the band reserves its space and
  // doesn't pop in as a block. Real title kept — only the data is shimmered.
  if (loading)
    return (
      <section
        className="cf-section cf-monitoring cf-monitoring--loading"
        aria-busy="true"
      >
        <div className="cf-section__head">
          <h2 className="cf-section__title">Monitoring — acquisition status</h2>
          <div className="cf-monitoring__kpis">
            <span className="cf-skel cf-skel--kpi" />
            <span className="cf-skel cf-skel--kpi" />
          </div>
        </div>
        <div className="cf-monitoring__body">
          <div className="cf-monitoring__total">
            <span className="cf-skel cf-skel--total" />
          </div>
          <div className="cf-monitoring__main">
            <span className="cf-skel cf-skel--bar" />
            <div className="cf-monitoring__legend">
              {[0, 1, 2, 3].map((i) => (
                <span key={i} className="cf-skel cf-skel--legend" />
              ))}
            </div>
          </div>
        </div>
      </section>
    );

  if (missions.length === 0) return null;

  const statusList = [
    ...new Set([
      ...MISSION_MONITORING_STATIC_STATUS,
      ...missions.map((mission) => mission.status),
    ]),
  ];

  // Drill level: top-level statuses, or the substatuses of the selected status.
  const drilled = selectedStatus !== undefined;
  const scope = drilled
    ? missions.filter((mission) => mission.status === selectedStatus)
    : missions;
  const keyOf = (mission) =>
    drilled ? mission[MiSSION_MONITORING_SUBSTATUS] : mission.status;
  const levelList = drilled ? [...new Set(scope.map(keyOf))] : statusList;

  const levelTotal = scope.length;
  const counts = {};
  scope.forEach((mission) => {
    const key = keyOf(mission);
    counts[key] = (counts[key] ?? 0) + 1;
  });
  const rows = levelList
    .filter((key) => counts[key])
    .map((key) => ({
      key,
      label: humanize(key),
      value: counts[key],
      pct: Math.round((counts[key] / levelTotal) * 100),
      color: getStatusColor(
        statusList,
        key,
        drilled ? levelList : [],
        selectedStatus
      ),
    }));

  const flags = sumKey(missions, MiSSION_MONITORING_FLAG_COUNT);
  const unread = sumKey(missions, MiSSION_MONITORING_COMMENT_COUNT);
  const total = missions.length;
  const delivered = missions.filter((m) => m.status === "DELIVERED").length;
  const deliveredPct = total ? Math.round((delivered / total) * 100) : 0;

  const drillProps = (key) =>
    drilled
      ? {}
      : {
          // Also narrows the site list: a status KPI that changes nothing
          // below it is what R1-11 reported.
          onClick: () => onSelectStatus?.(key),
          "data-tooltip-id": "tooltip",
          "data-tooltip-content": "Click to see substatuses",
        };

  return (
    <section className="cf-section cf-monitoring">
      <div className="cf-section__head">
        <h2 className="cf-section__title">
          Monitoring —{" "}
          {drilled
            ? `${humanize(selectedStatus)} substatuses`
            : "acquisition status"}
        </h2>
        <div className="cf-monitoring__kpis">
          <span>
            <i className="fa-solid fa-flag" /> {flags} active flag
            {flags === 1 ? "" : "s"}
          </span>
          <span>
            <i className="fa-solid fa-comment-dots" /> {unread} unread comment
            {unread === 1 ? "" : "s"}
          </span>
          {detailTo && (
            <Link
              className="btn btn-primary btn-sm cf-monitoring__detail"
              to={detailTo}
            >
              Open campaign monitoring <i className="fa-solid fa-arrow-right" />
            </Link>
          )}
        </div>
      </div>

      <div className="cf-monitoring__body">
        <div className="cf-monitoring__total">
          <span className="cf-monitoring__total-value">{deliveredPct}%</span>
          <span className="cf-monitoring__total-label">delivered</span>
          <span className="cf-monitoring__total-sub">
            {delivered}/{total}
          </span>
        </div>

        <div className="cf-monitoring__main">
          {/* Segments and legend rows share drillProps. The bar used to carry a
              hand-written copy of that click, so it drilled but never filtered
              once drillProps grew a second effect. The tooltip props sit after
              the spread so each segment keeps its own richer label. */}
          <div className="cf-monitoring__bar">
            {rows.map((row) => (
              <span
                key={row.key}
                className={
                  "cf-monitoring__seg" + (drilled ? "" : " is-clickable")
                }
                style={{ flexGrow: row.value, background: row.color }}
                {...drillProps(row.key)}
                data-tooltip-id="tooltip"
                data-tooltip-content={`${row.label}: ${row.value} (${row.pct}%)`}
              />
            ))}
          </div>

          <ul className="cf-monitoring__legend">
            {rows.map((row) => (
              <li
                className={
                  "cf-monitoring__row" + (drilled ? "" : " is-clickable")
                }
                key={row.key}
                {...drillProps(row.key)}
              >
                <span
                  className="cf-monitoring__dot"
                  style={{ background: row.color }}
                />
                <span className="cf-monitoring__name">{row.label}</span>
                <span className="cf-monitoring__val">{row.value}</span>
                <span className="cf-monitoring__pct">{row.pct}%</span>
              </li>
            ))}
            {/* Sits at the end of the legend, right-aligned: the control that
                clears the statuses belongs next to the statuses themselves. */}
            {drilled && (
              <li className="cf-monitoring__clear">
                <button
                  type="button"
                  className="cf-filters__clear cf-monitoring__back"
                  onClick={() => onSelectStatus?.(undefined)}
                >
                  <i className="fa-solid fa-arrow-left" /> Clear statuses
                </button>
              </li>
            )}
          </ul>
        </div>
      </div>
    </section>
  );
};

MonitoringRecap.propTypes = {
  missions: PropTypes.array.isRequired,
  detailTo: PropTypes.string,
  loading: PropTypes.bool,
  selectedStatus: PropTypes.string,
  onSelectStatus: PropTypes.func,
};
